import { Back, TweenMax } from 'gsap';
import { Container, Sprite } from 'pixi.js';
import { GameAudio } from '../../../Config/Audio';
import { getGameSize, IGameSizeInfo } from '../../../Config/Levels';
import SoundManager from '../../../Sound/SoundManager';
import { waitForMs } from '../../../Utils/AsyncUtils';
import { createOutlineText, scaleToFit } from '../../../Utils/Utils';
import { TextButton } from '../../Shared/Buttons/TextButton';
import { UIButton } from '../../Shared/Buttons/UIButton';
import { ScreenBlur } from '../../Shared/ScreenBlur';
import { ITweenable } from '../ITweenable';

/**
 * Base class for popups that show rewards. With a header, close button and confirm button.
 * @param width ITweenable width
 * @param height ITweenable height
 * @param headerStr Text that will appear in the header
 * @param buttonStr Text that will appear on the confirm button
 */
export abstract class BaseRewardPopup extends PIXI.Container implements ITweenable {
    protected main: Container;
    protected innerWidth: number;
    protected innerHeight: number;
    private overlay: ScreenBlur;

    constructor(width: number, height: number, headerStr: string, buttonStr: string) {
        super();

        this.innerWidth = width;
        this.innerHeight = height;

        const gameSize = getGameSize();
        this.main = new Container();
        this.main.x = (gameSize.gameWidth - this.innerWidth) / 2;
        this.main.y = gameSize.gameHeight;
        this.addChild(this.main);

        const texture = PIXI.utils.TextureCache['commonui/popup-bg'];
        const bg = new PIXI.mesh.NineSlicePlane(texture, 100, 100, 100, 100);
        bg.width = this.innerWidth;
        bg.height = this.innerHeight;
        this.main.addChild(bg);

        const character = new PIXI.Sprite(PIXI.utils.TextureCache['character/character-win']);
        character.anchor.set(0.5);
        character.x = this.innerWidth / 2;
        this.main.addChild(character);

        const header = new Sprite(PIXI.utils.TextureCache['commonui/header']);
        header.anchor.set(0.5);
        header.x = this.innerWidth / 2;
        header.y = this.innerHeight * 0.2;
        this.main.addChild(header);

        const headerText = createOutlineText(
            headerStr,
            new PIXI.Rectangle(this.innerWidth / 2, header.y, header.width, header.height * 0.4),
            'Baloo',
            40,
            35,
            0xffffff,
            0x991f33
        );
        headerText.scale.set(
            scaleToFit(headerText.width, headerText.height, width * 0.8, height * 0.15)
        );
        this.main.addChild(headerText);

        const playButton = new TextButton({
            width: width * 0.5,
            height: height * 0.2,
            btnText: buttonStr,
        });
        playButton.position.set(this.innerWidth / 2, this.innerHeight * 0.88);
        playButton.once('trigger', this.handlePopupClose.bind(this));
        this.main.addChild(playButton);

        const closeButton = new UIButton('commonui/button-close');
        closeButton.position.set(this.innerWidth - closeButton.width / 2, closeButton.height / 2);
        closeButton.interactive = true;
        closeButton.buttonMode = true;
        closeButton.once('trigger', this.handlePopupClose.bind(this));
        this.main.addChild(closeButton);
    }

    public async tweenIn() {
        if (!this.overlay) {
            this.overlay = new ScreenBlur();
            this.overlay.interactive = true;
            this.overlay.alpha = 0;
            this.addChildAt(this.overlay, 0);
            TweenMax.to(this.overlay, 0.6, { alpha: 1 });
        }

        const gameSize = getGameSize();
        TweenMax.to(this.main, 0.7, {
            y: gameSize.gameHeight * 0.2,
            delay: 0.3,
            ease: Back.easeOut.config(2),
        });
    }

    public async tweenOut() {
        this.overlay.fade(0, 600);
        TweenMax.to(this.main, 0.6, {
            y: getGameSize().gameHeight + (this.main.height - this.innerHeight),
            ease: Back.easeIn,
        });
        await waitForMs(600);
    }

    protected emitPopupCloseAction(): void {}

    private async handlePopupClose(event: PIXI.interaction.InteractionEvent) {
        SoundManager.playSound(GameAudio.TAP);
        this.emitPopupCloseAction();
    }

    public resize(sizeInfo: IGameSizeInfo) {
        if (this.overlay) {
            this.overlay.resize(sizeInfo);
        }
    }
}
